import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiSearch, FiMessageCircle, FiChevronRight } from 'react-icons/fi';
import { motion } from 'framer-motion';

export default function Friends() {
  const { users, friends = [], currentUser } = useAppContext();
  const navigate = useNavigate();

  const friendsList = useMemo(() => {
    return users
      .filter(u => friends.includes(u.id))
      .sort((a, b) => (a.isOnline === b.isOnline ? 0 : a.isOnline ? -1 : 1));
  }, [users, friends]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 pb-24 transition-colors">
      <div className="mb-8">
        <h1 className="text-3xl font-black text-brand-950 dark:text-white tracking-tight mb-1 uppercase">Your Nakama</h1>
        <p className="text-gray-400 dark:text-dark-muted font-bold uppercase tracking-widest text-[10px]">Manage your team and connections</p>
      </div>

      <div className="bg-white dark:bg-dark-surface rounded-[32px] border border-gray-100 dark:border-dark-border shadow-xl shadow-brand-900/5 overflow-hidden">
        <div className="p-6 border-b border-gray-50 dark:border-dark-border flex items-center justify-between bg-gray-50/30 dark:bg-dark-card/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-brand-200 dark:shadow-brand-900/20">
              <SafeIcon icon={FiUsers} />
            </div>
            <span className="font-black text-brand-950 dark:text-white text-sm uppercase tracking-widest">Friend List</span>
          </div>
          <span className="text-xs font-black text-brand-600 dark:text-brand-400 bg-white dark:bg-dark-surface px-3 py-1 rounded-full border border-brand-100 dark:border-brand-900/30">
            {friendsList.length} TOTAL
          </span>
        </div>

        <div className="divide-y divide-gray-50 dark:divide-dark-border">
          {friendsList.length > 0 ? (
            friendsList.map((friend, idx) => (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                key={friend.id} 
                onClick={() => navigate(`/profile/${friend.id}`)}
                className="flex items-center gap-4 p-5 hover:bg-brand-50 dark:hover:bg-dark-card cursor-pointer transition-all group"
              >
                <div className="relative shrink-0">
                  <img src={friend.avatar} className="w-14 h-14 rounded-2xl object-cover border-2 border-white dark:border-dark-surface shadow-md" alt="" />
                  <span className={`absolute -bottom-1 -right-1 w-4 h-4 border-2 border-white dark:border-dark-surface rounded-full ${friend.isOnline ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-black text-brand-950 dark:text-white truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors uppercase tracking-tight">
                    {friend.nickname}
                  </p>
                  <p className="text-[10px] text-gray-400 dark:text-dark-muted font-bold uppercase tracking-widest">
                    {friend.isOnline ? 'Online' : 'Offline'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-3 bg-gray-50 dark:bg-dark-card text-gray-400 rounded-xl group-hover:bg-white dark:group-hover:bg-dark-surface group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-all border border-transparent group-hover:border-brand-100 dark:group-hover:border-dark-border">
                    <SafeIcon icon={FiChevronRight} />
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="py-20 text-center px-10">
              <div className="w-16 h-16 bg-gray-50 dark:bg-dark-card rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300 dark:text-gray-700">
                <SafeIcon icon={FiUsers} className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-black text-brand-950 dark:text-white mb-1 uppercase">No Nakama yet</h3>
              <p className="text-sm text-gray-400 dark:text-dark-muted font-medium mb-6">Start exploring to find new teammates!</p>
              <button 
                onClick={() => navigate('/')}
                className="px-8 py-3 bg-brand-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-brand-200 dark:shadow-brand-900/20"
              >
                Go to Discovery
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}